import { useMediaQuery } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { Button } from '../../../stories/molecules';
import { ContainerDrawer } from '../../../stories/molecules/container-drawer/ContainerDrawer';
import { muiTheme } from '../../../theme';
import { useThemeContext } from '../../../global-state/themeContext';
import { areaOverviewTranslations } from '../../../translations/areaOverview';
import { commonTranslations, homeTranslations } from '../../../translations';
import { convertToKebabCase } from '../../../utils';
import { StyledTypography } from '../summaryWidget/SummaryWidget.styles';
import { ComparisonBadges, Comparison } from './comparisonBadges/ComparisonBadges';
export type { Comparison } from './comparisonBadges/ComparisonBadges';

type Statistics = {
  label: string;
  value: string;
  comparison?: Comparison;
};

type OverviewWidgetType = {
  id: string;
  title: string;
  headerIcon: Icons;
  isLoading: boolean;
  isError: boolean;

  noWeeklyData?: boolean;
  noMonthlyData?: boolean;

  headerText?: string;
  headerValue?: string;
  headerComparison?: Comparison;
  widgetText?: string;
  statistics?: Statistics[];
  customContent?: React.ReactNode;
  sliderContent?: React.ReactNode;
  errorRetryHandler?: () => void;
};

export const OverviewWidget: FC<OverviewWidgetType> = ({
  id,
  title,
  headerIcon,
  isLoading,
  isError,

  noWeeklyData,
  noMonthlyData,

  headerText,
  headerValue,
  headerComparison,
  widgetText,
  statistics,
  customContent,
  sliderContent,
  errorRetryHandler,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const [isSliderOpen, setIsSliderOpen] = useState(false);

  const widgetBackgroundColor = mode === 'dark' ? 'backgroundPaperDark' : 'white';
  const metricBackgroundColor = mode === 'dark' ? 'backgroundSelectedDark' : 'backgroundTableHeader';

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));

  const errorView = isError && !isLoading && !noWeeklyData && !noMonthlyData;
  const loadingView = isLoading && !isError && !noWeeklyData && !noMonthlyData;
  const noWeeklyView = noWeeklyData && !noMonthlyData && !isLoading && !isError;
  const noMonthlyView = noMonthlyData && !noWeeklyData && !isLoading && !isError;
  const contentView = !noWeeklyView && !noMonthlyView && !isLoading && !isError;

  const { performance } = areaOverviewTranslations;

  if (loadingView) {
    return (
      <ColumnFlexContainer
        key={id}
        backgroundColor={widgetBackgroundColor}
        shadow
        borderRadius={[4]}
        height={[78]}
        justifyContent="center"
        alignItems="center"
        gap={[2]}
        data-testid="loading-area-view"
      >
        <Icon icon={Icons.SPINNER} size="small" />
        <Typography type="body2">
          {t(homeTranslations.common.loading.key, homeTranslations.common.loading.defaultValue)}...
        </Typography>
      </ColumnFlexContainer>
    );
  }

  if (errorView) {
    return (
      <ColumnFlexContainer
        key={id}
        backgroundColor={widgetBackgroundColor}
        shadow
        borderRadius={[4]}
        height={[78]}
        justifyContent="center"
        alignItems="center"
        gap={[2]}
        data-testid="error-area-view"
      >
        <Typography type="h4">
          {t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
        </Typography>
        <Typography type="body2">
          {t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
        </Typography>
        <Button
          label={t(commonTranslations.retry.key, commonTranslations.retry.defaultValue)}
          onClick={() => errorRetryHandler && errorRetryHandler()}
          buttonContentProps={{ iconOptions: { customIcon: <Icon icon={Icons.REFRESH} /> } }}
        />
      </ColumnFlexContainer>
    );
  }

  const widgetContent = (
    <ColumnFlexContainer
      key={id}
      data-testid={`${convertToKebabCase(id, true)}-widget-wrapper`}
      backgroundColor={widgetBackgroundColor}
      padding={isSmallDesktop ? [3] : [3, 4]}
      shadow
      borderRadius={[4]}
      height={[78]}
    >
      <RowFlexContainer gap={[2]} margin={[0, 0, 2]} alignItems="center" justifyContent="between">
        <RowFlexContainer gap={[2]} alignItems="center">
          <Icon icon={headerIcon} size={isSmallDesktop ? 'small' : 'medium'} />
          <Typography type={isTablet ? 'body3' : 'button'}>{title}</Typography>
        </RowFlexContainer>
        {sliderContent && (
          <RowFlexContainer
            role="button"
            aria-label={t(performance.buttonLabel.key, performance.buttonLabel.defaultValue)}
            cursor="pointer"
            alignItems="center"
            gap={[1]}
            hasBorder
            padding={isMobile ? [1] : [1, 2]}
            borderRadius={[4]}
            onClick={() => setIsSliderOpen(true)}
            data-testid="performance-button"
          >
            <Icon icon={Icons.MAP} size="small" />
            {!isMobile && (
              <Typography type="body3">
                {t(performance.buttonLabel.key, performance.buttonLabel.defaultValue)}
              </Typography>
            )}
          </RowFlexContainer>
        )}
      </RowFlexContainer>

      {noWeeklyView && (
        <ColumnFlexContainer justifyContent="center" alignItems="center" height="100%">
          <Typography type="button">No weekly data available for this widget</Typography>
        </ColumnFlexContainer>
      )}

      {noMonthlyView && (
        <ColumnFlexContainer justifyContent="center" alignItems="center" height="100%">
          <Typography type="button">No monthly data available for this widget</Typography>
        </ColumnFlexContainer>
      )}

      {contentView && (
        <>
          {customContent ? (
            customContent
          ) : (
            <>
              <RowFlexContainer alignItems="center" gap={[2]} flexWrap="wrap">
                <Typography type="h4" margin={isMobile ? [0, 0, 1] : [0]}>
                  {headerValue}
                </Typography>
                {headerComparison && <ComparisonBadges comparison={headerComparison} />}
              </RowFlexContainer>
              <Typography type={isTablet ? 'caption' : 'body2'}>{headerText}</Typography>
              <ContainerV2 style={{ margin: 'auto 0' }}>
                <Typography type="body2">{widgetText}</Typography>
              </ContainerV2>
              {statistics && !!statistics.length && (
                <ContainerV2
                  display="flex"
                  flexDirection={isSmallDesktop ? 'column' : 'row'}
                  gap={[2]}
                  data-testid="statistics-container"
                >
                  {statistics.map((metric, index) => (
                    <ColumnFlexContainer
                      key={index}
                      padding={[3, 2]}
                      gap={[1]}
                      backgroundColor={metricBackgroundColor}
                      borderRadius={[2]}
                      flex={1}
                    >
                      <RowFlexContainer alignItems="center" justifyContent="between">
                        <StyledTypography type={isTablet ? 'caption' : 'body2'}>{metric.label}</StyledTypography>
                        {!isMobile && metric.comparison && <ComparisonBadges comparison={metric.comparison} />}
                      </RowFlexContainer>
                      <Typography type="body2">{metric.value}</Typography>
                    </ColumnFlexContainer>
                  ))}
                </ContainerV2>
              )}
            </>
          )}
        </>
      )}
    </ColumnFlexContainer>
  );

  if (sliderContent) {
    return (
      <ContainerDrawer
        isOpen={isSliderOpen}
        onClose={() => setIsSliderOpen(false)}
        sliderContent={sliderContent}
        width={'300px'}
        borderRadius={[4]}
      >
        {widgetContent}
      </ContainerDrawer>
    );
  }

  return widgetContent;
};
